/**
 * Statistics Service
 * Handles fetching statistics data for a given invoice proposal number
 */

import axios from 'axios';

/**
 * Fetches statistics data from the backend webservice
 *
 * @param {string} facvstnr - Invoice proposal number
 * @returns {Promise<Object>} The statistics data response
 * @throws {Error} Throws Error('FORBIDDEN') on 403, rethrows other errors as-is
 */
export const fetchStatisticsData = async (facvstnr, deflvnr) => {
  try {
    // facvstnr alone does not identify a settlement: in drfvsrp 25.922 DEFVSNR
    // values belong to more than one supplier, so the same number exists under
    // several issuing entities. DEFVSNR + DEFLVNR has no collisions at all.
    // deflvnr is left off the query string while it is absent, so the request
    // is unchanged until the documents list starts exposing it.
    const params = deflvnr ? { facvstnr, deflvnr } : { facvstnr };
    const response = await axios.get('/jsonapi/document_details_afrekeningsborderel', {
      params,
    });
    return response.data;
  } catch (err) {
    if (err.response && err.response.status === 403) {
      throw new Error('FORBIDDEN');
    }
    throw err;
  }
};

/**
 * Fetches benchmarking data for a given supplier and week range.
 * Filter the result for type === 'drgmbmk' to get benchmark reference records.
 *
 * @param {string|number} supplierId
 * @param {number} fromWeek
 * @param {number} fromYear
 * @param {number} toWeek
 * @param {number} toYear
 * @returns {Promise<Array>} Raw array of benchmark records
 * @throws {Error} Throws Error('FORBIDDEN') on 403, rethrows other errors as-is
 */
export const fetchBenchmarkData = async (supplierId, fromWeek, fromYear, toWeek, toYear) => {
  try {
    const response = await axios.get('/jsonapi/benchmarking_get_data', {
      params: {
        supplierid: supplierId,
        from_week: fromWeek,
        from_year: fromYear,
        to_week: toWeek,
        to_year: toYear,
      },
    });
    return response.data;
  } catch (err) {
    if (err.response && err.response.status === 403) {
      throw new Error('FORBIDDEN');
    }
    throw err;
  }
};

export default {
  fetchStatisticsData,
  fetchBenchmarkData,
};
